import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'

import { BREAKPOINTS } from '../lib/breakpoints'
import { ASSISTANT_ENABLED } from '../lib/features'
import { ADMIN_NAV, productNav } from '../lib/nav'
import { useMediaQuery } from '../lib/useMediaQuery'
import { AssistantDock } from './assistant/AssistantDock'
import { SideNav } from './SideNav'
import { PageEndContext } from './pageEndContext'
import { ThemeToggle } from './ThemeToggle'
import { Avatar, Icon } from './ui'

interface AppShellProps {
  children: ReactNode
  counts?: { pendingCount?: number }
  /** Hides the admin destinations for a non-administrator (§5.8). */
  showAdminNav?: boolean
  /**
   * Shows State, third in the product list, when the reading on `/home` carries
   * a state map (1.9.9 P6). Off by default: the shell never guesses that one does.
   */
  showStateNav?: boolean
  account?: { email: string | null; role: string; experiments_enabled_for_me?: boolean }
  onSignOut?: () => void
}

/**
 * The branded shell: a persistent rail on a desktop, a drawer on a phone.
 *
 * Sprint 1.8's spec asks for a desktop-first layout and explicitly excludes
 * mobile navigation. The sprint owner overrode that on 2026-09-11 — phone
 * responsiveness is in scope from the first commit — so the rail collapses
 * rather than disappearing.
 *
 * **A drawer, not a bottom tab bar.** Three destinations do not earn a tab bar,
 * and porting AIVY's would drag in the surface-stack machinery that exists to
 * keep four chat surfaces mounted at once — the wrong shape here, and something
 * §2.3 rules out for good reasons that still hold.
 *
 * On a phone the drawer is a real `<dialog>` opened with `showModal()`, which
 * is what makes it correct rather than merely styled: the focus trap, Escape to
 * close, the inert background and the backdrop are the platform's, not
 * something re-implemented here and subtly wrong. The nav list itself is one
 * component rendered in whichever container applies.
 *
 * **From 1024px there is no top bar at all** (Sprint 1.9.1, R24). Identity, the
 * theme control and sign-out live in `ShellFooter`, which the rail renders; a
 * top bar there would be a second, emptier copy of the same row. Below 1024 the
 * bar is the menu trigger, the compact wordmark and `.shell__account` — the
 * avatar and, for an operator, the Admin pill, which is the only cue the admin
 * console exists at a width where the rail is gone.
 */
export function AppShell({
  children,
  counts,
  account,
  onSignOut,
  showAdminNav = true,
  showStateNav = false,
}: AppShellProps) {
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [pageEnd, setPageEnd] = useState<HTMLDivElement | null>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const { pathname } = useLocation()

  const closeDrawer = useCallback(() => setDrawerOpen(false), [])

  // Drive the dialog from state rather than calling showModal() in the handler,
  // so the two cannot disagree after a route change or a resize.
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (drawerOpen && !dialog.open) dialog.showModal()
    else if (!drawerOpen && dialog.open) dialog.close()

    /**
     * THE CASE `handleDialogClose` CANNOT COVER, and it is the same one
     * `ConfirmDialog.tsx` documents for itself: a conditionally rendered
     * `<dialog>` is UNMOUNTED, not closed. No `close` event fires, so `onClose`
     * never runs.
     *
     * The drawer renders only below the desktop breakpoint, so crossing that
     * line with it open -- which on a phone is a rotation, not a resize --
     * removes an open modal dialog from the document. Measured before this:
     * `document.activeElement` was `<body>` afterwards.
     *
     * The trigger is gone too at that width, so there is nothing to restore TO
     * and focus has to be MOVED. `#main` is the honest target: it is where the
     * rail's navigation leads and it is already `tabIndex={-1}` for the skip
     * link, so nothing new is added to the tab order.
     */
    return () => {
      if (!dialog.open) return
      dialog.close()
      // READ AT CLEANUP TIME, deliberately, which is what the lint rule below
      // is warning about and what this code wants. React detaches refs before
      // running this cleanup, so on an UNMOUNT `triggerRef.current` is already
      // null and the fallback applies -- which is correct, because at desktop
      // width the trigger does not exist. On an ordinary close the component is
      // still mounted and the ref still points at the button. Copying the value
      // into the effect, as the rule suggests, would capture the trigger and
      // then focus a detached node.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const fallback = triggerRef.current ?? document.getElementById('main')
      fallback?.focus()
    }
  }, [drawerOpen])

  // Close the drawer when the layout crosses the breakpoint, and when the route
  // changes. Both are adjustments DURING RENDER rather than effects, which is
  // React's documented answer to "reset state when an input changes" and what
  // `react-hooks/set-state-in-effect` is pointing at. An effect would run a
  // frame later -- long enough for the drawer to paint as a modal on top of the
  // rail it was just replaced by.
  const [lastLayout, setLastLayout] = useState(isDesktop)
  if (lastLayout !== isDesktop) {
    setLastLayout(isDesktop)
    if (drawerOpen) setDrawerOpen(false)
  }

  const [lastPath, setLastPath] = useState(pathname)
  if (lastPath !== pathname) {
    setLastPath(pathname)
    if (drawerOpen) setDrawerOpen(false)
  }

  // Return focus to the trigger when the drawer closes -- including when the
  // platform closes it for us, via Escape or the backdrop.
  const handleDialogClose = useCallback(() => {
    setDrawerOpen(false)
    triggerRef.current?.focus()
  }, [])

  return (
    <div className="shell">
      <a className="skip-link" href="#main">Skip to content</a>

      {isDesktop && (
        <aside className="shell__rail">
          <Brand />
          {/* Two navigations, distinctly NAMED. An administrator sees both, and
              two landmarks called the same thing are indistinguishable to
              anyone moving between them -- which is why SideNav's `label` is
              required rather than defaulted.

              The wrapper is a plain <div> and adds NO landmark, so R24's
              ['Main', 'Admin'] is untouched. It exists so the rail's scroll
              context can sit around the part that grows -- the nav -- instead
              of around the whole column, which is what let `/home` push the
              "Not a medical device." line off the bottom of a 720px viewport.
              `.shell__rail-nav` in shell.css carries the argument. */}
          <div className="shell__rail-nav">
            <SideNav items={productNav({ state: showStateNav, experiments: account?.experiments_enabled_for_me === true })} label="Main" />
            {showAdminNav && <SideNav items={ADMIN_NAV} label="Admin" counts={counts} />}
          </div>
          <ShellFooter account={account} onSignOut={onSignOut} />
        </aside>
      )}

      <div className="shell__body">
        {/* NOT RENDERED from 1024px. The rail carries identity, theme and
            sign-out there, so a top bar would be a second, emptier copy of the
            same row. The switch is this expression rather than a CSS
            `display: none`: the rail/drawer switch is already JS (`isDesktop`
            above), and a `.shell__topbar { display: none }` block would also be
            a `.shell__topbar` rule with no inset longhands in it. */}
        {!isDesktop && (
          <header className="shell__topbar">
            <button
              ref={triggerRef}
              type="button"
              className="btn btn--icon"
              aria-expanded={drawerOpen}
              aria-haspopup="dialog"
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
              <span className="visually-hidden">Open navigation</span>
            </button>
            <Brand compact />
            <div className="shell__topbar-end">
              {/* The avatar AND the Admin pill. Below 1024 the rail is gone, so
                  this pill is the only cue that the operator console is
                  reachable. No `title`: it carried the email, which now lives
                  in the drawer footer and on /account, and a title on a wrapper
                  of `aria-hidden` content is announced inconsistently. */}
              {account && (
                <span className="shell__account">
                  <Avatar email={account.email} />
                  {account.role === 'admin' && <span className="pill pill--cyan">Admin</span>}
                </span>
              )}
            </div>
          </header>
        )}

        <main className="shell__main" id="main" tabIndex={-1}>
          <PageEndContext.Provider value={pageEnd}>{children}</PageEndContext.Provider>
          {/* R73: the end of the page, after the routed screen. `PageEnd` puts
              research notes here, so they follow everything the page shows. */}
          <div className="page-end" ref={setPageEnd} />
        </main>
      </div>

      {!isDesktop && (
        <dialog ref={dialogRef} className="drawer" onClose={handleDialogClose} aria-label="Navigation">
          <div className="drawer__head">
            <Brand />
            <button type="button" className="btn btn--icon" onClick={closeDrawer}>
              <CloseIcon />
              <span className="visually-hidden">Close navigation</span>
            </button>
          </div>
          <SideNav items={productNav({ state: showStateNav, experiments: account?.experiments_enabled_for_me === true })} label="Main" onNavigate={closeDrawer} />
          {showAdminNav && (
            <SideNav items={ADMIN_NAV} label="Admin" counts={counts} onNavigate={closeDrawer} />
          )}
          <div className="drawer__foot">
            <ShellFooter account={account} onSignOut={onSignOut} />
          </div>
        </dialog>
      )}

      {/* agent-1-web: the assistant's bubble and drawer, the shell's LAST child
          so the desktop drawer takes the third grid track. Behind a flag read
          at module scope: a build without the assistant carries none of it. */}
      {ASSISTANT_ENABLED && <AssistantDock isDesktop={isDesktop} />}
    </div>
  )
}

function Brand({ compact = false }: { compact?: boolean }) {
  // Two files, not one recoloured: the wordmark's ink differs per theme and the
  // text is outlined, so it renders without DM Sans having loaded. The
  // <picture> switches on the theme attribute via CSS, which keeps the swap out
  // of JavaScript and therefore out of the first paint.
  return (
    <div className={`brand${compact ? ' brand--compact' : ''}`}>
      <img className="brand__mark brand__mark--dark" src="/wordmark-on-dark.svg" alt="BrainSpot" />
      <img className="brand__mark brand__mark--light" src="/wordmark-on-light.svg" alt="" aria-hidden="true" />
    </div>
  )
}

/**
 * Identity, theme and sign-out, in the rail foot and in the drawer foot.
 *
 * From Sprint 1.9.1 this is the ONLY place the theme control and the sign-out
 * button live: the desktop top bar is gone (R24), so both moved down here
 * rather than being duplicated. `<ThemeToggle />` is mounted once, inside this
 * component, which is what keeps `getAllByRole('group', { name: 'Theme' })` at
 * one per rendering.
 *
 * Sign-out is an icon button with an `aria-label`, not a labelled one: the row
 * it shares with the theme control is 220px wide inside the rail, and the word
 * would either wrap or push the three glyphs under 44px on a touch device.
 */
function ShellFooter({
  account,
  onSignOut,
}: {
  account?: { email: string | null; role: string }
  onSignOut?: () => void
}) {
  return (
    <div className="shell__foot">
      {account && (
        <div className="shell__foot-account">
          <Avatar email={account.email} />
          <span className="shell__foot-text">
            <span className="shell__foot-email">{account.email ?? 'Signed in'}</span>
            <span className="shell__foot-role">{account.role}</span>
          </span>
        </div>
      )}
      <div className="shell__foot-row">
        <ThemeToggle />
        {onSignOut && (
          <button type="button" className="btn btn--icon" aria-label="Sign out" onClick={onSignOut}>
            <Icon name="logout" />
          </button>
        )}
      </div>
      <p className="shell__trust">Wellness and self-awareness tool. Not a medical device.</p>
    </div>
  )
}

/* Lucide `menu` and `x`, inlined. The design system vendors a curated subset the
   same way; installing lucide-react for two glyphs is 1.4 MB of dependency for
   ~40 bytes of path data. */
function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}
